import styled, { css, keyframes } from "styled-components";

const FONT_BODY = '"Manrope", "IBM Plex Sans", "Avenir Next", sans-serif';
const FONT_DISPLAY = '"Space Grotesk", "IBM Plex Sans", "Avenir Next", sans-serif';
const FONT_EDITORIAL = '"DM Serif Text", "Bodoni Moda", serif';

export const PageRoot = styled.main`
  position: relative;
  min-height: 100vh;
  color: #2b4a3b;
  font-family: ${FONT_BODY};
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  background: #f3faf5;
`;

export const SectionContainer = styled.div`
  width: min(1160px, calc(100% - 48px));
  margin: 0 auto;
  display: grid;
  grid-template-columns: ${(props) => (props.vertical ? "1fr" : "0.96fr 1.04fr")};
  gap: ${(props) => (props.vertical ? "28px" : "54px")};

  @media (max-width: 992px) {
    width: min(1160px, calc(100% - 34px));
    grid-template-columns: 1fr;
    gap: 28px;
  }
`;

export const TopBanner = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(142px, 17vw, 186px) 0 clamp(76px, 9vw, 104px);
  border-top: 1px solid #d2e5d8;
  border-bottom: 1px solid #d2e5d8;
  background: #14221b;

  ${(props) =>
    props.$darkOverlay
      ? css`
          box-shadow: inset 0 -90px 150px rgba(0, 0, 0, 0.56);

          &::before {
            content: "";
            position: absolute;
            inset: 0;
            z-index: 1;
            pointer-events: none;
            background: linear-gradient(
              180deg,
              rgba(0, 0, 0, 0.26) 0%,
              rgba(0, 0, 0, 0.48) 100%
            );
          }
        `
      : css`
          &::before {
            content: none;
          }
        `}
`;

export const TopBannerBackdrop = styled.div`
  position: absolute;
  inset: -14% 0;
  z-index: 0;
  pointer-events: none;
  will-change: transform;
  background: url(${(props) => props.$image || "/images/fundo%20verde.jpg"}) no-repeat center
    center / cover;
  transform: scale(1.08);
`;

export const TopBannerInner = styled.div`
  position: relative;
  z-index: 2;
  width: min(1120px, calc(100% - 48px));
  margin: 0 auto;
  display: grid;
  gap: 14px;

  @media (max-width: 992px) {
    width: min(1120px, calc(100% - 34px));
  }
`;

export const TopBannerEyebrow = styled.div`
  display: inline-block;
  width: fit-content;
  font-family: ${FONT_DISPLAY};
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(239, 247, 242, 0.9);
  text-shadow: 0 2px 10px rgba(7, 12, 9, 0.55);
`;

export const TopBannerTitle = styled.h1`
  margin: 6px 0 0;
  font-family: ${FONT_DISPLAY};
  font-size: clamp(2.3rem, 6.2vw, 5.1rem);
  line-height: 0.94;
  letter-spacing: -0.024em;
  max-width: 14ch;
  color: #f3f8f5;
  text-shadow: 0 8px 26px rgba(7, 12, 9, 0.58);
  text-wrap: balance;
`;

export const TopBannerText = styled.p`
  margin: 8px 0 0;
  max-width: 62ch;
  font-family: ${FONT_BODY};
  font-size: clamp(1rem, 1.28vw, 1.08rem);
  line-height: 1.72;
  color: rgba(230, 239, 234, 0.92);
  text-shadow: 0 4px 16px rgba(7, 12, 9, 0.42);
`;

export const SectionBlock = styled.section`
  padding: clamp(84px, 9vw, 116px) 0;
  background: ${(props) => (props.light ? "#f8fdf9" : "transparent")};

  ${(props) =>
    props.light
      ? css`
          border-top: 1px solid #d2e5d8;
          border-bottom: 1px solid #d2e5d8;
        `
      : ""}
`;

export const SectionEyebrow = styled.div`
  display: inline-block;
  font-family: ${FONT_DISPLAY};
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${(props) => (props.$inverse ? "#dfeee6" : "#6d897a")};
`;

export const SectionTitle = styled.h2`
  margin: 8px 0 10px;
  font-family: ${FONT_EDITORIAL};
  font-size: clamp(1.9rem, 4vw, 3.3rem);
  line-height: 1.06;
  letter-spacing: -0.01em;
  max-width: 16ch;
  color: ${(props) => (props.$inverse ? "#f3f9f5" : "#234133")};
  text-wrap: balance;
`;

export const SectionText = styled.p`
  margin: 0;
  max-width: 64ch;
  font-family: ${FONT_BODY};
  font-size: clamp(0.98rem, 1.18vw, 1.04rem);
  line-height: 1.72;
  color: ${(props) => (props.$inverse ? "rgba(239, 247, 242, 0.9)" : "#597366")};
`;

export const StepsGrid = styled.div`
  margin-top: 34px;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 740px) {
    grid-template-columns: 1fr;
  }
`;

export const StepCard = styled.article`
  grid-column: ${(props) => (props.$wide ? "span 6" : "span 3")};
  min-height: 206px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background:
    linear-gradient(
      120deg,
      rgba(6, 20, 14, 0.54) 0%,
      rgba(8, 24, 17, 0.46) 55%,
      rgba(6, 20, 14, 0.56) 100%
    ),
    url(${(props) => props.$image || "/images/fundo%20verde.jpg"}) no-repeat center center / cover;
  box-shadow: 0 10px 20px rgba(8, 24, 17, 0.2);
  padding: 22px 20px 18px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  :hover {
    border-color: rgba(255, 255, 255, 0.52);
    box-shadow: 0 14px 24px rgba(8, 24, 17, 0.26);
  }

  ${(props) =>
    props.$uniform
      ? ""
      : css`
          :nth-child(1),
          :nth-child(6) {
            grid-column: span 6;
          }
        `}

  @media (max-width: 1180px) {
    grid-column: span 1;
    min-height: 220px;

    ${(props) =>
      props.$uniform
        ? ""
        : css`
            :nth-child(1),
            :nth-child(6) {
              grid-column: span 1;
            }
          `}
  }
`;

export const StepNumber = styled.div`
  width: auto;
  height: auto;
  border-radius: 0;
  background: transparent;
  color: rgba(245, 252, 248, 0.92);
  font-weight: 600;
  font-size: 0.76rem;
  letter-spacing: 0.08em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
`;

export const StepTitle = styled.h3`
  margin: 12px 0 8px;
  max-width: 20ch;
  font-family: ${FONT_DISPLAY};
  font-size: 1rem;
  line-height: 1.32;
  letter-spacing: 0.01em;
  color: #ffffff;
`;

export const StepText = styled.p`
  margin: 0;
  font-family: ${FONT_BODY};
  color: rgba(239, 248, 242, 0.9);
  line-height: 1.62;
  font-size: 0.92rem;
`;

export const PostOperativeFeatureGrid = styled.div`
  margin-top: 34px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

export const PostOperativeFeatureCard = styled.article`
  min-height: 244px;
  padding: clamp(24px, 3vw, 32px);
  border: 1px solid rgba(126, 161, 143, 0.42);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.58);
  display: flex;
  flex-direction: column;
`;

export const PostOperativeFeatureMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const PostOperativeFeatureLabel = styled.span`
  color: #668273;
  font-family: ${FONT_DISPLAY};
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.11em;
  line-height: 1.4;
  text-transform: uppercase;
`;

export const PostOperativeFeatureTitle = styled.h3`
  margin: 34px 0 12px;
  max-width: 18ch;
  color: #234936;
  font-family: ${FONT_EDITORIAL};
  font-size: clamp(1.45rem, 2.5vw, 2.05rem);
  line-height: 1.08;
  letter-spacing: -0.015em;
  text-wrap: balance;
`;

export const PostOperativeFeatureText = styled.p`
  margin: 0;
  max-width: 58ch;
  color: #557263;
  font-family: ${FONT_BODY};
  font-size: 0.95rem;
  line-height: 1.72;
`;

export const PostOperativeTreatments = styled.ul`
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: clamp(28px, 5vw, 72px);

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

export const PostOperativeTreatmentItem = styled.li`
  position: relative;
  min-height: 58px;
  padding: 17px 8px 17px 22px;
  border-bottom: 1px solid rgba(108, 145, 126, 0.35);
  color: #315644;
  font-family: ${FONT_BODY};
  font-size: 0.94rem;
  line-height: 1.5;
  display: flex;
  align-items: center;

  &::before {
    content: "";
    position: absolute;
    left: 2px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #608c75;
  }
`;

export const ServicesGrid = styled.div`
  margin-top: 38px;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 740px) {
    grid-template-columns: 1fr;
  }
`;

export const ServiceCard = styled.article`
  grid-column: span 3;
  overflow: hidden;
  border-radius: 26px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.66);
  box-shadow:
    0 18px 36px rgba(62, 127, 101, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(12px);
  transition: transform 0.24s ease, border-color 0.24s ease, box-shadow 0.24s ease;

  :hover {
    transform: translateY(-5px);
    border-color: rgba(166, 228, 199, 0.72);
    box-shadow:
      0 26px 44px rgba(62, 127, 101, 0.24),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  @media (max-width: 1180px) {
    grid-column: span 1;
  }
`;

export const ServiceImage = styled.div`
  height: clamp(220px, 28vw, 330px);
  background:
    radial-gradient(circle at 20% 20%, rgba(188, 238, 215, 0.84) 0%, rgba(188, 238, 215, 0) 48%),
    radial-gradient(circle at 84% 72%, rgba(138, 219, 188, 0.5) 0%, rgba(138, 219, 188, 0) 44%),
    linear-gradient(145deg, rgba(246, 254, 250, 0.95) 0%, rgba(221, 245, 233, 0.92) 52%, rgba(195, 234, 215, 0.9) 100%);
  transition: transform 0.34s ease;

  ${ServiceCard}:hover & {
    transform: scale(1.04);
  }
`;

export const ServiceTitle = styled.h3`
  margin: 18px 18px 6px;
  font-family: ${FONT_DISPLAY};
  font-size: 1.04rem;
  line-height: 1.32;
  letter-spacing: 0.01em;
  color: #245e45;
`;

export const ServiceText = styled.p`
  margin: 0 18px 22px;
  color: #4f705e;
  line-height: 1.72;
  font-size: 0.96rem;
`;

export const RankingSection = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(84px, 9vw, 116px) 0;
  border-top: 1px solid #d2e5d8;
  border-bottom: 1px solid #d2e5d8;
  background:
    linear-gradient(
      140deg,
      rgba(9, 20, 14, 0.58) 0%,
      rgba(14, 31, 22, 0.52) 50%,
      rgba(8, 19, 13, 0.6) 100%
    ),
    url("/images/fundo verde.jpg") no-repeat center center / cover;
  box-shadow: inset 0 -70px 120px rgba(0, 0, 0, 0.4);

  ${SectionEyebrow} {
    color: rgba(223, 238, 230, 0.92);
  }

  ${SectionTitle} {
    color: #eff8f2;
  }

  ${SectionText} {
    color: rgba(232, 243, 236, 0.9);
  }
`;

export const RankingGrid = styled.div`
  margin-top: 34px;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 740px) {
    grid-template-columns: 1fr;
  }
`;

export const RankingCard = styled.article`
  grid-column: span 4;
  min-height: 124px;
  border-radius: 14px;
  border: 1px solid #cde1d4;
  background: transparent;
  box-shadow: 0 2px 8px rgba(24, 36, 29, 0.04);
  padding: 18px 20px;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  :hover {
    border-color: #a9c6b6;
    box-shadow: 0 4px 10px rgba(24, 36, 29, 0.06);
  }

  @media (max-width: 1180px) {
    grid-column: span 1;
  }
`;

export const RankingPosition = styled.strong`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${FONT_DISPLAY};
  font-size: 0.86rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.12);
`;

export const RankingInfo = styled.div`
  display: grid;
  gap: 4px;
`;

export const RankingTitle = styled.h3`
  margin: 0;
  font-family: ${FONT_DISPLAY};
  font-size: 0.98rem;
  line-height: 1.3;
  letter-spacing: 0.01em;
  color: #ffffff;
`;

export const RankingMeta = styled.p`
  margin: 0;
  font-family: ${FONT_BODY};
  font-size: 0.9rem;
  line-height: 1.54;
  color: rgba(240, 248, 243, 0.92);
`;

export const TreatmentsSection = styled.section`
  position: relative;
  padding: clamp(84px, 9vw, 116px) 0;
  background:
    linear-gradient(
      120deg,
      rgba(6, 20, 14, 0.62) 0%,
      rgba(8, 24, 17, 0.54) 55%,
      rgba(6, 20, 14, 0.64) 100%
    ),
    url("/images/hafundo.jpg") no-repeat center center / cover;

  ${SectionEyebrow} {
    color: rgba(245, 252, 248, 0.9);
  }

  ${SectionTitle} {
    color: #ffffff;
  }

  ${SectionText} {
    color: rgba(240, 248, 243, 0.92);
  }
`;

export const TreatmentsGrid = styled.div`
  margin-top: 34px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
`;

export const TreatmentCard = styled.article`
  flex: 1 1 248px;
  max-width: 272px;
  width: 100%;
  min-height: 166px;
  border-radius: 14px;
  border: 1px solid #d6e6dd;
  background: #ffffff;
  box-shadow: 0 8px 18px rgba(8, 24, 17, 0.16);
  padding: 18px 18px 16px;
  display: flex;
  flex-direction: column;
  transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;

  :hover {
    border-color: #b6cfbf;
    transform: translateY(-3px);
    box-shadow: 0 14px 24px rgba(8, 24, 17, 0.22);
  }

  @media (max-width: 740px) {
    max-width: 100%;
    flex-basis: 100%;
  }

  ${(props) =>
    props.$centerCta
      ? css`
          flex-basis: 100%;
          max-width: 620px;
          margin-inline: auto;
          min-height: 188px;
          align-items: center;
          text-align: center;
        `
      : ""}
`;

export const TreatmentName = styled.h3`
  margin: 0 0 8px;
  font-family: ${FONT_DISPLAY};
  font-size: 0.98rem;
  line-height: 1.3;
  letter-spacing: 0.01em;
  color: #2b4a3b;
  text-wrap: balance;
`;

export const TreatmentDescription = styled.p`
  margin: 0;
  font-family: ${FONT_BODY};
  font-size: 0.9rem;
  line-height: 1.56;
  color: #648174;
`;

export const TreatmentButton = styled.button`
  margin-top: auto;
  align-self: flex-start;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid #9ec7b2;
  background: #edf7f1;
  color: #4f7765;
  font-family: ${FONT_DISPLAY};
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;

  :hover {
    background: #6c9f85;
    border-color: #6c9f85;
    color: #f7faf8;
    transform: translateY(-1px);
  }

  ${(props) =>
    props.$centerCta
      ? css`
          align-self: center;
        `
      : ""}
`;

export const BeforeAfterSection = styled.section`
  padding: clamp(84px, 9vw, 114px) 0;
  border-top: 1px solid rgba(90, 168, 138, 0.16);
  border-bottom: 1px solid rgba(90, 168, 138, 0.16);
  background: rgba(255, 255, 255, 0.34);
`;

export const BeforeAfterGrid = styled.div`
  margin-top: 32px;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`;

export const BeforeAfterCard = styled.article`
  grid-column: span 4;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr auto;
  gap: 12px;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.66);
  box-shadow:
    0 18px 36px rgba(62, 127, 101, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(12px);
  padding: 16px;
  transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;

  :hover {
    transform: translateY(-4px);
    border-color: rgba(166, 228, 199, 0.72);
    box-shadow:
      0 24px 40px rgba(62, 127, 101, 0.22),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  @media (max-width: 1180px) {
    grid-column: span 1;
  }
`;

export const BeforeAfterHead = styled.header`
  grid-column: 1 / -1;
  display: grid;
  gap: 4px;
  min-height: 72px;
`;

export const BeforeAfterTitle = styled.h3`
  margin: 0;
  color: #235f46;
  font-family: ${FONT_DISPLAY};
  font-size: 1rem;
  line-height: 1.3;
  letter-spacing: 0.01em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const BeforeAfterMeta = styled.p`
  margin: 0;
  color: #5a7a68;
  font-family: ${FONT_BODY};
  font-size: 0.88rem;
  line-height: 1.54;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const BeforeAfterPair = styled.figure`
  margin: 0;
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.78);
  height: 220px;
  box-shadow: 0 10px 20px rgba(62, 127, 101, 0.14);

  @media (max-width: 620px) {
    height: 220px;
  }
`;

export const BeforeAfterTrack = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.32s ease;
`;

export const BeforeAfterSlide = styled.div`
  min-width: 100%;
  height: 100%;
  position: relative;
`;

export const BeforeAfterImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

export const BeforeAfterLabel = styled.figcaption`
  position: absolute;
  top: 10px;
  left: 10px;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.7);
  background: rgba(26, 68, 48, 0.44);
  backdrop-filter: blur(8px);
  color: #f3fff8;
  font-family: ${FONT_DISPLAY};
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const BeforeAfterControls = styled.div`
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: inline-flex;
  gap: 6px;
`;

export const BeforeAfterNav = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.64);
  background: rgba(20, 56, 40, 0.56);
  color: #f4fff8;
  font-family: ${FONT_DISPLAY};
  font-size: 0.78rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  :hover {
    background: rgba(20, 56, 40, 0.72);
    transform: translateY(-1px);
  }
`;

export const BeforeAfterDots = styled.div`
  min-height: 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const BeforeAfterDot = styled.button`
  width: 7px;
  height: 7px;
  border-radius: 999px;
  border: 0;
  padding: 0;
  cursor: pointer;
  background: ${(props) => (props.$active ? "#2f7a5a" : "rgba(94, 140, 117, 0.36)")};
  transition: transform 0.2s ease, opacity 0.2s ease;

  :hover {
    transform: scale(1.08);
    opacity: 0.92;
  }
`;

export const AestheticGrid = styled.div`
  margin-top: 38px;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 740px) {
    grid-template-columns: 1fr;
  }
`;

export const AestheticCard = styled.article`
  grid-column: span 4;
  border-radius: 26px;
  overflow: hidden;
  border: 1px solid ${(props) => (props.$inverse ? "rgba(230, 249, 238, 0.36)" : "rgba(255, 255, 255, 0.8)")};
  background: ${(props) => (props.$inverse ? "rgba(247, 253, 249, 0.9)" : "rgba(255, 255, 255, 0.66)")};
  box-shadow:
    0 18px 36px rgba(62, 127, 101, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(12px);
  transition: transform 0.24s ease, border-color 0.24s ease, box-shadow 0.24s ease;

  :nth-child(2),
  :nth-child(7) {
    grid-column: span 8;
  }

  :hover {
    transform: translateY(-5px);
    border-color: rgba(166, 228, 199, 0.72);
    box-shadow:
      0 26px 44px rgba(62, 127, 101, 0.24),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  @media (max-width: 1180px) {
    grid-column: span 1;

    :nth-child(2),
    :nth-child(7) {
      grid-column: span 1;
    }
  }
`;

export const AestheticImage = styled.div`
  height: clamp(220px, 24vw, 310px);
  background:
    linear-gradient(180deg, rgba(17, 62, 42, 0.05) 0%, rgba(17, 62, 42, 0.4) 100%),
    url(${(props) => props.image}) no-repeat center center / cover;
`;

export const AestheticTag = styled.div`
  margin: 16px 18px 8px;
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  font-family: ${FONT_DISPLAY};
  border: 1px solid ${(props) => (props.$inverse ? "rgba(90, 168, 138, 0.34)" : "rgba(90, 168, 138, 0.28)")};
  background: rgba(239, 252, 245, 0.78);
  color: #2f7457;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const AestheticTitle = styled.h3`
  margin: 8px 18px 8px;
  font-family: ${FONT_DISPLAY};
  color: #245e45;
  font-size: clamp(1.14rem, 1.7vw, 1.42rem);
  line-height: 1.26;
`;

export const AestheticText = styled.p`
  margin: 0 18px 20px;
  color: #4f705e;
  line-height: 1.72;
  font-size: 0.95rem;
`;

export const OfferSection = styled.section`
  padding: clamp(24px, 4vw, 42px) 0 clamp(72px, 8vw, 96px);
`;

export const OfferCard = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: end;
  gap: 28px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: url("/images/fundo verde.jpg") no-repeat center center / cover;
  box-shadow: none;
  padding: clamp(24px, 4vw, 40px);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    align-items: stretch;
    gap: 20px;
  }
`;

export const OfferLead = styled.div`
  margin-bottom: 10px;
  font-family: ${FONT_DISPLAY};
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: rgba(245, 252, 248, 0.92);
`;

export const OfferTitle = styled.h3`
  margin: 0;
  max-width: 18ch;
  font-family: ${FONT_DISPLAY};
  letter-spacing: -0.01em;
  line-height: 1.08;
  color: #ffffff;
  font-size: clamp(1.5rem, 2.8vw, 2.2rem);
  text-wrap: balance;
`;

export const OfferText = styled.p`
  margin: 14px 0 0;
  max-width: 58ch;
  font-size: clamp(0.96rem, 1.16vw, 1.02rem);
  line-height: 1.66;
  color: rgba(241, 249, 244, 0.94);
`;

export const OfferButton = styled.button`
  margin-top: 0;
  min-height: 46px;
  padding: 0 24px;
  border: 1px solid #6c9f85;
  border-radius: 999px;
  background: #6c9f85;
  color: #f7faf8;
  font-family: ${FONT_DISPLAY};
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.22s ease, color 0.22s ease, transform 0.22s ease;

  :hover {
    transform: translateY(-1px);
    background: #5b8d74;
  }

  @media (max-width: 900px) {
    width: 100%;
  }
`;

export const LocationSection = styled.section`
  padding: 0 0 clamp(84px, 9vw, 108px);
`;

export const LocationCard = styled.div`
  display: grid;
  grid-template-columns: 0.92fr 1.08fr;
  gap: 22px;
  border-radius: 14px;
  border: 1px solid #cde1d4;
  background: #f8fdf9;
  padding: clamp(18px, 3vw, 28px);

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const LocationInfo = styled.div`
  display: grid;
  align-content: start;
  gap: 10px;
`;

export const LocationLead = styled.div`
  font-family: ${FONT_DISPLAY};
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: #6d897a;
`;

export const LocationTitle = styled.h3`
  margin: 0;
  font-family: ${FONT_EDITORIAL};
  font-size: clamp(1.5rem, 2.7vw, 2.1rem);
  line-height: 1.1;
  color: #2a4a3b;
  text-wrap: balance;
`;

export const LocationAddress = styled.p`
  margin: 0;
  font-size: 0.98rem;
  line-height: 1.68;
  color: #597366;
`;

export const LocationAction = styled.button`
  margin-top: 4px;
  width: fit-content;
  min-height: 42px;
  padding: 0 18px;
  border: 1px solid #6c9f85;
  border-radius: 999px;
  background: #6c9f85;
  color: #f7faf8;
  font-family: ${FONT_DISPLAY};
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.22s ease, transform 0.22s ease;

  :hover {
    transform: translateY(-1px);
    background: #5b8d74;
  }
`;

export const LocationMap = styled.div`
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #cde1d4;
  min-height: 320px;

  @media (max-width: 700px) {
    min-height: 280px;
  }
`;

export const LocationMapFrame = styled.iframe`
  width: 100%;
  height: 100%;
  min-height: inherit;
  border: 0;
  display: block;
`;

export const LocationFootnote = styled.p`
  margin: 14px 0 0;
  text-align: center;
  font-size: 0.9rem;
  line-height: 1.6;
  color: #678376;
`;

export const AboutSection = styled.section`
  position: relative;
  padding: clamp(84px, 9vw, 116px) 0 58px;
  background: url("/images/fundo verde.jpg") no-repeat center center / cover;

  ${SectionEyebrow} {
    color: rgba(244, 251, 247, 0.92);
  }
`;

export const AboutPhoto = styled.img`
  width: 100%;
  min-height: 520px;
  object-fit: cover;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow: none;

  @media (max-width: 992px) {
    min-height: 360px;
  }
`;

export const AboutBody = styled.div`
  align-self: center;
`;

export const AboutName = styled.h2`
  margin: 10px 0 12px;
  font-family: ${FONT_EDITORIAL};
  font-size: clamp(1.9rem, 3.6vw, 2.9rem);
  letter-spacing: -0.01em;
  line-height: 1.1;
  color: #ffffff;
  text-wrap: balance;
`;

export const AboutParagraph = styled.p`
  margin: 0;
  font-size: 0.98rem;
  line-height: 1.68;
  color: rgba(241, 249, 244, 0.94);
`;

export const HighlightsGrid = styled.div`
  margin-top: 28px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

export const HighlightCard = styled.div`
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.34);
  background: rgba(10, 28, 18, 0.22);
  box-shadow: none;
  padding: 16px;
`;

export const HighlightValue = styled.strong`
  display: block;
  font-family: ${FONT_DISPLAY};
  color: #ffffff;
  font-size: 1.12rem;
  letter-spacing: 0.01em;
`;

export const HighlightLabel = styled.span`
  margin-top: 7px;
  display: block;
  color: rgba(239, 248, 242, 0.9);
  font-size: 0.88rem;
  line-height: 1.5;
`;

export const FeedbackSection = styled.section`
  padding: clamp(84px, 9vw, 116px) 0 74px;
  border-top: 1px solid #d2e5d8;
  background: #f8fdf9;
`;

export const FeedbackHeader = styled.div`
  max-width: 900px;
`;

export const FeedbackGrid = styled.div`
  margin-top: 28px;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const FeedbackActionWrap = styled.div`
  margin-top: 42px;
  display: flex;
  justify-content: center;
`;

const feedbackButtonPulse = keyframes`
  0%,
  100% {
    transform: translateY(0) scale(1);
    box-shadow: 0 0 0 0 rgba(108, 159, 133, 0.38);
  }

  50% {
    transform: translateY(-1px) scale(1.02);
    box-shadow: 0 0 0 12px rgba(108, 159, 133, 0);
  }
`;

export const FeedbackActionButton = styled.button`
  min-height: 52px;
  padding: 0 30px;
  border: 1px solid #6c9f85;
  border-radius: 999px;
  background: #6c9f85;
  color: #f7faf8;
  font-family: ${FONT_DISPLAY};
  font-size: 0.76rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 10px 20px rgba(62, 127, 101, 0.24);
  animation: ${feedbackButtonPulse} 2.2s ease-in-out infinite;
  transition: transform 0.22s ease, background 0.22s ease, box-shadow 0.22s ease;

  :hover {
    animation-play-state: paused;
    transform: translateY(-2px) scale(1.01);
    background: #5b8d74;
    box-shadow: 0 14px 24px rgba(62, 127, 101, 0.3);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const FeedbackCard = styled.article`
  grid-column: span 6;
  min-height: 280px;
  padding: 26px 22px 20px;
  border-radius: 14px;
  border: 1px solid #cde1d4;
  background: #f8fdf9;
  box-shadow: 0 2px 8px rgba(24, 36, 29, 0.04);
  display: flex;
  flex-direction: column;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  :hover {
    border-color: #a9c6b6;
    box-shadow: 0 4px 10px rgba(24, 36, 29, 0.06);
  }

  :nth-child(2n) {
    transform: translateY(26px);
  }

  @media (max-width: 980px) {
    grid-column: span 1;

    :nth-child(2n) {
      transform: none;
    }
  }
`;

export const FeedbackStars = styled.div`
  color: #f4c542;
  font-size: 0.9rem;
  letter-spacing: 0.1em;
  margin-bottom: 12px;
`;

export const FeedbackQuote = styled.p`
  margin: 0;
  color: #5f796c;
  font-size: clamp(1rem, 1.24vw, 1.08rem);
  line-height: 1.66;
`;

export const FeedbackAuthor = styled.strong`
  margin-top: auto;
  padding-top: 20px;
  font-size: 0.86rem;
  font-family: ${FONT_DISPLAY};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #2f5141;
`;

export const FeedbackMeta = styled.span`
  margin-top: 5px;
  color: #6f8a7c;
  font-size: 0.8rem;
  letter-spacing: 0.03em;
`;

export const InstagramStrip = styled.section`
  margin-top: 14px;
  padding: 0 0 80px;
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  overflow: auto;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(4, minmax(160px, 1fr));
    padding: 0 20px 70px;
  }
`;

export const InstagramItem = styled.img`
  width: 100%;
  border-radius: 18px;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.78);
  box-shadow: 0 14px 24px rgba(62, 127, 101, 0.14);
`;
